import React, { FC, useRef } from "react";
import { useTranslation } from "react-i18next";

import {
    CheckPicker,
    DatePicker,
    IconButton,
    InputNumber,
    Popover,
    SelectPicker,
    Table as RsuiteTable,
    Tooltip,
    Whisper,
} from "rsuite";

import CheckIcon from "@rsuite/icons/Check";
import HelpOutlineIcon from "@rsuite/icons/esm/react/HelpOutline";
import TrashIcon from "@rsuite/icons/Trash";

import constants from "utils/constants";

import { useDayList } from "hooks/useDayList";

import {
    ScheduleIntervalType,
    SchedulerTime,
} from "store/environments/tenants/tenant-scheduler/tenant-scheduler-types";

import {
    combineWeekdays,
    defaultTimeOfDay,
    defaultInterval,
    formatTimeOfDay,
    getCurrentUtcTime,
    getSelectedWeekdays,
    parseIntervalInput,
    parseTimeOfDay,
} from "../scheduler-helpers";

import "./Table.less";

export type TableItem<T> = T & {
    key: string;
    isValid?: boolean;
    errors?: Record<string, string> | null;
};

const headerHeight = 40;
const rowHeight = 56;

interface Props {
    items: TableItem<SchedulerTime>[];
    isExtended: boolean;
    disabled: boolean;
    emptyText: string;
    onRowChange: (item: TableItem<SchedulerTime>) => void;
    onRowDelete: (item: TableItem<SchedulerTime>) => void;
}

const HeaderHelp: FC<{ text: string }> = ({ text }) => (
    <Whisper
        trigger="hover"
        placement="top"
        speaker={<Tooltip>{text}</Tooltip>}
    >
        <HelpOutlineIcon className="scheduler-table__help" />
    </Whisper>
);

const Table: FC<Props> = ({
    items,
    isExtended,
    disabled,
    emptyText,
    onRowChange,
    onRowDelete,
}: Props) => {
    const { t } = useTranslation();

    const ref = useRef<HTMLDivElement>(null);

    const intervalTypeOptions = [
        {
            label: t("tenantConfig.scheduler.intervalType.time"),
            value: ScheduleIntervalType.TimeOfDay,
        },
        {
            label: t("tenantConfig.scheduler.intervalType.interval"),
            value: ScheduleIntervalType.Minutes,
        },
    ];

    const dayOptions = useDayList(0).map(({ value, label }) => ({
        value,
        label,
    }));

    const isDeleteDisabled = disabled || items.length <= 1;

    const handleIntervalTypeChange = (
        value: ScheduleIntervalType | null,
        rowData: TableItem<SchedulerTime>
    ) => {
        if (value === null) {
            return;
        }

        onRowChange({
            ...rowData,
            intervalType: value,
            intervalInMinutes:
                value === ScheduleIntervalType.Minutes ? defaultInterval : null,
            timeOfDay:
                value === ScheduleIntervalType.TimeOfDay
                    ? defaultTimeOfDay
                    : null,
        });
    };

    const handleIntervalChange = (
        value: string | number | null,
        rowData: TableItem<SchedulerTime>
    ) => {
        const intervalInMinutes = parseIntervalInput(value);

        if (intervalInMinutes === undefined) {
            return;
        }

        onRowChange({ ...rowData, intervalInMinutes });
    };

    const handleTimeOfDayChange = (
        date: Date | null,
        rowData: TableItem<SchedulerTime>
    ) => {
        if (!date) {
            return;
        }

        onRowChange({ ...rowData, timeOfDay: formatTimeOfDay(date) });
    };

    const handleWeekdaysChange = (
        values: number[],
        rowData: TableItem<SchedulerTime>
    ) => {
        onRowChange({ ...rowData, weekdays: combineWeekdays(values) });
    };

    const handleDelete = (rowData: TableItem<SchedulerTime>) => {
        onRowDelete(rowData);
    };

    const renderError = (
        rowData: TableItem<SchedulerTime>,
        field: string,
        children: React.ReactElement
    ) => (
        <Whisper
            trigger="none"
            placement="bottom"
            container={() => ref.current!}
            open={!!rowData.errors?.[field]}
            speaker={
                <Popover className="editable-cell__error-text">
                    {rowData.errors?.[field]}
                </Popover>
            }
        >
            <div className="scheduler-table__field">{children}</div>
        </Whisper>
    );

    return (
        <div ref={ref} className="scheduler-table">
            <RsuiteTable
                data={items}
                headerHeight={headerHeight}
                rowHeight={rowHeight}
                autoHeight
                renderEmpty={() => (
                    <div className="scheduler-table__empty">{emptyText}</div>
                )}
            >
                <RsuiteTable.Column width={80} align="center" fixed>
                    <RsuiteTable.HeaderCell>
                        {t("tenantConfig.scheduler.table.active")}
                    </RsuiteTable.HeaderCell>
                    <RsuiteTable.Cell dataKey="id">
                        {(rowData: TableItem<SchedulerTime>) =>
                            rowData.id ? <CheckIcon /> : null
                        }
                    </RsuiteTable.Cell>
                </RsuiteTable.Column>

                {isExtended && (
                    <RsuiteTable.Column flexGrow={1} minWidth={140}>
                        <RsuiteTable.HeaderCell>
                            {t("tenantConfig.scheduler.table.type")}
                            <HeaderHelp
                                text={t(
                                    "tenantConfig.scheduler.table.typeTooltip"
                                )}
                            />
                        </RsuiteTable.HeaderCell>
                        <RsuiteTable.Cell dataKey="intervalType">
                            {(rowData: TableItem<SchedulerTime>) => (
                                <div className="scheduler-table__field">
                                    <SelectPicker
                                        container={() => ref.current!}
                                        data={intervalTypeOptions}
                                        value={rowData.intervalType}
                                        cleanable={false}
                                        searchable={false}
                                        disabled={disabled}
                                        onChange={(value) =>
                                            handleIntervalTypeChange(
                                                value,
                                                rowData
                                            )
                                        }
                                    />
                                </div>
                            )}
                        </RsuiteTable.Cell>
                    </RsuiteTable.Column>
                )}

                {isExtended && (
                    <RsuiteTable.Column flexGrow={1} minWidth={140}>
                        <RsuiteTable.HeaderCell>
                            {t("tenantConfig.scheduler.table.interval")}
                            <HeaderHelp
                                text={t(
                                    "tenantConfig.scheduler.table.intervalTooltip"
                                )}
                            />
                        </RsuiteTable.HeaderCell>
                        <RsuiteTable.Cell dataKey="intervalInMinutes">
                            {(rowData: TableItem<SchedulerTime>) =>
                                rowData.intervalType ===
                                ScheduleIntervalType.Minutes ? (
                                    renderError(
                                        rowData,
                                        "intervalInMinutes",
                                        <InputNumber
                                            postfix="min"
                                            min={
                                                constants.minSchedulerIntervalLimit
                                            }
                                            max={
                                                constants.maxSchedulerIntervalValueLimit
                                            }
                                            value={
                                                rowData.intervalInMinutes ?? ""
                                            }
                                            disabled={disabled}
                                            onChange={(value) =>
                                                handleIntervalChange(
                                                    value,
                                                    rowData
                                                )
                                            }
                                        />
                                    )
                                ) : (
                                    <span className="scheduler-table__muted">
                                        {t(
                                            "tenantConfig.scheduler.intervalDefault"
                                        )}
                                    </span>
                                )
                            }
                        </RsuiteTable.Cell>
                    </RsuiteTable.Column>
                )}

                <RsuiteTable.Column flexGrow={1} minWidth={140}>
                    <RsuiteTable.HeaderCell>
                        {t("tenantConfig.scheduler.table.time")}
                        <HeaderHelp
                            text={t("tenantConfig.scheduler.table.timeTooltip")}
                        />
                    </RsuiteTable.HeaderCell>
                    <RsuiteTable.Cell dataKey="timeOfDay">
                        {(rowData: TableItem<SchedulerTime>) =>
                            rowData.intervalType ===
                            ScheduleIntervalType.TimeOfDay ? (
                                <div className="scheduler-table__field">
                                    <DatePicker
                                        container={() => ref.current!}
                                        format="HH:mm"
                                        value={parseTimeOfDay(
                                            rowData.timeOfDay
                                        )}
                                        cleanable={false}
                                        disabled={disabled}
                                        ranges={[
                                            {
                                                label: t(
                                                    "tenantConfig.scheduler.now"
                                                ),
                                                value: () =>
                                                    getCurrentUtcTime(),
                                            },
                                        ]}
                                        onChange={(date) =>
                                            handleTimeOfDayChange(date, rowData)
                                        }
                                    />
                                </div>
                            ) : (
                                <span className="scheduler-table__muted">
                                    {t(
                                        "tenantConfig.scheduler.timePickerPlaceholder"
                                    )}
                                </span>
                            )
                        }
                    </RsuiteTable.Cell>
                </RsuiteTable.Column>

                <RsuiteTable.Column flexGrow={2} minWidth={220}>
                    <RsuiteTable.HeaderCell>
                        {t("tenantConfig.scheduler.table.day")}
                        <HeaderHelp
                            text={t("tenantConfig.scheduler.table.dayTooltip")}
                        />
                    </RsuiteTable.HeaderCell>
                    <RsuiteTable.Cell dataKey="weekdays">
                        {(rowData: TableItem<SchedulerTime>) =>
                            renderError(
                                rowData,
                                "weekdays",
                                <CheckPicker
                                    container={() => ref.current!}
                                    placement="bottomStart"
                                    data={dayOptions}
                                    value={getSelectedWeekdays(
                                        rowData.weekdays
                                    )}
                                    placeholder={t(
                                        "tenantConfig.scheduler.daySelectorPlaceholder"
                                    )}
                                    searchable={false}
                                    cleanable={false}
                                    countable={false}
                                    disabled={disabled}
                                    onChange={(values) =>
                                        handleWeekdaysChange(values, rowData)
                                    }
                                />
                            )
                        }
                    </RsuiteTable.Cell>
                </RsuiteTable.Column>

                <RsuiteTable.Column width={80} align="center">
                    <RsuiteTable.HeaderCell>
                        {t("tenantConfig.scheduler.table.action")}
                    </RsuiteTable.HeaderCell>
                    <RsuiteTable.Cell>
                        {(rowData: TableItem<SchedulerTime>) => (
                            <IconButton
                                appearance="subtle"
                                size="sm"
                                icon={<TrashIcon />}
                                disabled={isDeleteDisabled}
                                onClick={() => handleDelete(rowData)}
                            />
                        )}
                    </RsuiteTable.Cell>
                </RsuiteTable.Column>
            </RsuiteTable>
        </div>
    );
};

export default Table;
